import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGate } from '@/lib/rbac/Gate';
import { PermissionButton } from '@/lib/ui/Button';
import { ContentStatusPill } from '@/lib/ui/StatusPill';
import { Dialog } from '@/lib/ui/Dialog';
import { Button } from '@/lib/ui/Button';
import { TextInput } from '@/lib/ui/Field';
import { MonoValue } from '@/lib/ui/Typography';
import { formatDateTime } from '@/lib/format';
import type { ContentStatus } from '@/lib/api/types';

/**
 * Lifecycle controls (spec §9.0): publish now / schedule / unpublish, gated by
 * content:publish. Scheduled items show the target time with reschedule/cancel.
 */
export function PublishControls({
  status,
  scheduledAt,
  onPublish,
  onUnpublish,
  busy,
}: {
  status: ContentStatus;
  scheduledAt: string | null;
  onPublish: (scheduledAt?: string) => void;
  onUnpublish: () => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const { allowed, reason } = useGate('content:publish');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [when, setWhen] = useState('');

  return (
    <div className="flex flex-wrap items-center gap-sm">
      <ContentStatusPill status={status} />
      {status === 'scheduled' && scheduledAt && (
        <span className="font-mono text-label-sm text-ink-mute">
          → <MonoValue>{formatDateTime(scheduledAt)}</MonoValue>
        </span>
      )}

      {status !== 'published' && (
        <PermissionButton
          allowed={allowed}
          reason={reason}
          size="sm"
          loading={busy}
          onClick={() => onPublish()}
        >
          {t('publish.publishNow')}
        </PermissionButton>
      )}
      <PermissionButton
        allowed={allowed}
        reason={reason}
        variant="ghost"
        size="sm"
        onClick={() => setScheduleOpen(true)}
      >
        {status === 'scheduled' ? t('publish.reschedule') : t('common.schedule')}
      </PermissionButton>
      {(status === 'published' || status === 'scheduled') && (
        <PermissionButton
          allowed={allowed}
          reason={reason}
          variant="ghost"
          size="sm"
          loading={busy}
          onClick={onUnpublish}
        >
          {t('common.unpublish')}
        </PermissionButton>
      )}

      <Dialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        title={t('publish.scheduleDialogTitle')}
        description={t('publish.scheduleDialogDesc')}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setScheduleOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={!when}
              onClick={() => {
                onPublish(new Date(when).toISOString());
                setScheduleOpen(false);
              }}
            >
              {t('common.schedule')}
            </Button>
          </>
        }
      >
        <TextInput
          type="datetime-local"
          label={t('publish.publishAt')}
          value={when}
          onChange={(e) => setWhen(e.target.value)}
        />
      </Dialog>
    </div>
  );
}
